import React, { useEffect, useState } from 'react';
import { Avatar, IconButton } from '@mui/material';
import { createPortal } from 'react-dom';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

import { Persona } from '../../lib/api/Persona';
import { Source } from '../../lib/models/Chat';
import { ChatUiMessage } from '../../lib/interfaces/Chat';
import { ClassificationBadge } from '../../components/ClassificationBadge/ClassificationBadge';
import { SubgraphModal } from '../../components/Graph';
import { MarkdownContent } from '../../components/Chat/MarkdownContent';
import { isScoutPersona, resolveScoutGuideProfile, ScoutPondering } from './scout';
import { FeedbackButtons } from '../../components/Chat/FeedbackButtons';
import type { FeedbackCategoryId } from '@common/domain/types';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { stripExtraTags } from '@common/domain/tag-filter';
import { WebSearchBadge } from '../../components/Chat/WebSearchBadge';
import { SynapseBadge } from '../../components/Chat/SynapseBadge';
import { AudioPlayerPopout } from '../../components/Chat/AudioPlayer';
import { isWebSearchResponseMode, wasSynapseUsed } from '../../lib/helpers/chatUtils';
import { TypingDots } from '../../components/Chat/TypingDots';

// Citation File Viewer Imports
import {
    DocxViewer,
    urlContext,
    highlightTextContext,
    sourceContext,
    citationViewerOpenContext
} from '../../components/Chat/CitationViewer';

/** Shown under an answer when the server reported a failure after it began
 *  streaming. The text above it may be complete (only its saving failed) or
 *  cut short, so the notice claims neither. */
const STREAM_INTERRUPTED_NOTICE = 'This reply may not have been saved.';

export interface ChatBubbleProps {
    message: ChatUiMessage;
    showClassification?: boolean;
    showWorkflowSteps?: boolean;
    userEmail?: string;
    persona?: Persona | null | undefined;
    onFeedback?: (exchangeId: string, kind: 'up' | 'down' | null, comment?: string, chatId?: string, categories?: FeedbackCategoryId[]) => void;
    /** Real Dynamo exchange id + conversation id; must match text reply storage */
    onSpeak?: (text: string, exchangeId?: string, chatId?: string) => void;
    onStopSpeak?: () => void;
    currentlyPlayingId?: string | null;
    chatId?: string;
    returnSpeak?: (text: string, messageId?: string) => Promise<HTMLAudioElement | undefined>;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
    message,
    showClassification,
    showWorkflowSteps = true,
    userEmail,
    persona,
    onFeedback,
    onSpeak,
    onStopSpeak,
    currentlyPlayingId,
    chatId,
    returnSpeak,
}) => {
    const { userInfo } = useUserInfoContext();
    const [graphModalOpen, setGraphModalOpen] = useState(false);
    const [expandedWorkflowLayer, setExpandedWorkflowLayer] = useState<string | null>(null);
    const [showPlanningTrace, setShowPlanningTrace] = useState(false);
    const [planningTraceIndex, setPlanningTraceIndex] = useState(0);
    const [workflowOpen, setWorkflowOpen] = useState(false);

    // need for citation file viewer
    const [url, setUrl] = useState("");
    const [highlightText, setHighlightText] = useState("");
    const [source, setSource] = useState<Source | null>(null);
    const [citationViewerOpen, setCitationViewerOpen] = useState(false);

    // need for AudioPlayer
    const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
    const [audioOpen, setAudioOpen] = useState(false);

    const handleAudioClose = () => {
        if (audioEl) {
            audioEl.pause();
            // Revoke blob URL to free memory
            if (audioEl.src.startsWith('blob:')) {
                URL.revokeObjectURL(audioEl.src);
            }
        }
        setAudioEl(null);
        setAudioOpen(false);
    };
    type WorkflowLayerStatus = 'complete' | 'active' | 'pending' | 'skipped';

    type WorkflowLayer = {
        name: string;
        status: WorkflowLayerStatus;
        details: string[];
    };

    const getWorkflowLayerName = (step: string): string | null => {
        const normalized = step.toLowerCase();

        if (normalized.includes('request')) return 'Request';
        if (normalized.includes('safety')) return 'Safety Check';

        // Keep re-ranking/context selection before retrieval so ranking events do not get swallowed by retrieval.
        if (
            normalized.includes('rerank') ||
            normalized.includes('rerankstep') ||
            normalized.includes('re-rank') ||
            normalized.includes('re ranking') ||
            normalized.includes('ranking') ||
            normalized.includes('ranked') ||
            normalized.includes('context selection') ||
            normalized.includes('selecting context') ||
            normalized.includes('selected context') ||
            normalized.includes('evidence selection') ||
            normalized.includes('candidate context')
        ) {
            return 'Re-ranking / Context Selection';
        }
        if (
            normalized.includes('retrieval') ||
            normalized.includes('retrieved') ||
            normalized.includes('search') ||
            normalized.includes('searching') ||
            normalized.includes('context') ||
            normalized.includes('knowledge') ||
            normalized.includes('chunk') ||
            normalized.includes('chunks')
        ) {
            return 'Retrieval';
        }

        if (
            normalized.includes('claim') ||
            normalized.includes('claims') ||
            normalized.includes('write gate') ||
            normalized.includes('write-gate') ||
            normalized.includes('alignment') ||
            normalized.includes('alignmentdecision') ||
            normalized.includes('alignmentdecisionstep') ||
            normalized.includes('grounding') ||
            normalized.includes('supported') ||
            normalized.includes('unsupported') ||
            normalized.includes('verification') ||
            normalized.includes('validate') ||
            normalized.includes('validated')
        ) {
            return 'Claim / Write Gate';
        }

        if (normalized.includes('generating') || normalized.includes('response generated')) {
            return 'Response Generation';
        }

        if (
            normalized.includes('alignment') ||
            normalized.includes('source') ||
            normalized.includes('final') ||
            normalized.includes('complete')
        ) {
            return 'Final Review';
        }

        return null;
    };

    const getDefaultWorkflowDetails = (
        layerName: string,
        status: WorkflowLayerStatus,
        message: ChatUiMessage
    ): string[] => {
        const retrievedCount = message.retrievedChunkIds?.length ?? 0;
        const usedCount = message.usedChunkIds?.length ?? 0;
        const workflowMetadata = message.workflowMetadata as any;
    

        if (layerName === 'Request') {
            return [
                'Received the user message.',
                'Attached chat settings and selected persona.',
                'Started the response workflow.',
            ];
        }

        if (layerName === 'Safety Check') {
            const safety = (
                message.safetyClassification ||
                workflowMetadata?.safetyClassification ||
                message.safetyRoute
            ) as any;

            const retrievalDecision = (
                message.retrievalDecision ||
                workflowMetadata?.retrievalDecision ||
                safety?.retrieval
            ) as any;

            return [
            'Classified the request before continuing.',
            safety?.category ? `Classification: ${safety.category}` : null,
            safety?.intent ? `Intent: ${safety.intent}` : null,
            safety?.confidence !== undefined ? `Confidence: ${safety.confidence}` : null,
            safety?.reason ? `Reason: ${safety.reason}` : null,
            retrievalDecision?.isFactQuery !== undefined
                ? `Retrieval needed: ${retrievalDecision.isFactQuery ? 'Yes' : 'No'}`
                : null,
            retrievalDecision?.factQueryType
                ? `Fact query type: ${retrievalDecision.factQueryType}`
                : null,
            retrievalDecision?.targetClaim
                ? `Target claim: ${retrievalDecision.targetClaim}`
                : null,
            Array.isArray(retrievalDecision?.queryVariants) && retrievalDecision.queryVariants.length > 0
                ? `Query variants: ${retrievalDecision.queryVariants.join(', ')}`
                : null,
            retrievalDecision?.confidence !== undefined
                ? `Retrieval confidence: ${retrievalDecision.confidence}`
                : null,
            retrievalDecision?.rationale
                ? `Retrieval rationale: ${retrievalDecision.rationale}`
                : null,
        ].filter(Boolean) as string[];
        }

        if (layerName === 'Retrieval') {
            const retrievalSummary = (message.retrievalSummary || workflowMetadata?.retrievalSummary) as any;
            const retrievalDecision = (message.retrievalDecision || workflowMetadata?.retrievalDecision) as any;

            const totalRetrieved = retrievalSummary?.retrievedChunkCount ?? retrievedCount;
            const totalUsed = retrievalSummary?.usedChunkCount ?? usedCount;

            if (totalRetrieved > 0) {
                return [
                    `Retrieved ${totalRetrieved} context chunk${totalRetrieved === 1 ? '' : 's'}.`,
                    `Used ${totalUsed} chunk${totalUsed === 1 ? '' : 's'} in the final response.`,
                    retrievalDecision?.needsWindowedContext !== undefined
                        ? `Windowed context needed: ${retrievalDecision.needsWindowedContext ? 'Yes' : 'No'}`
                        : 'Windowed context decision was not returned to the UI.',
                    retrievalDecision?.requiresExactGrounding !== undefined
                        ? `Exact grounding required: ${retrievalDecision.requiresExactGrounding ? 'Yes' : 'No'}`
                        : 'Exact grounding decision was not returned to the UI.',
                    'Prepared retrieved context for response grounding.',
                ];
            }

            return status === 'complete'
                ? ['Retrieval ran, but no supporting context chunks were returned.']
                : status === 'skipped'
                    ? ['No retrieval was needed for this response.']
                    : ['Searching for relevant context when needed.'];
        }
        

        if (layerName === 'Re-ranking / Context Selection') {
            const retrievalSummary = (message.retrievalSummary || workflowMetadata?.retrievalSummary) as any;
            const alignment = (message.alignment || workflowMetadata?.alignment) as any;

            return status === 'skipped'
                ? ['No re-ranking was needed because there was no retrieved context to rank.']
                : [
                    `Reviewed ${retrievalSummary?.retrievedChunkCount ?? retrievedCount} candidate context chunk${(retrievalSummary?.retrievedChunkCount ?? retrievedCount) === 1 ? '' : 's'}.`,
                    `Selected ${retrievalSummary?.usedChunkCount ?? usedCount} chunk${(retrievalSummary?.usedChunkCount ?? usedCount) === 1 ? '' : 's'} for the final response.`,
                    alignment?.topCosSim !== undefined
                        ? `Top similarity: ${alignment.topCosSim}`
                        : 'Top similarity was not returned to the UI.',
                    alignment?.avgCosSim !== undefined
                        ? `Average similarity: ${alignment.avgCosSim}`
                        : 'Average similarity was not returned to the UI.',
                    alignment?.supportCount !== undefined
                        ? `Support count: ${alignment.supportCount}`
                        : 'Support count was not returned to the UI.',
                    'Prepared the strongest supporting context for generation.',
                ];
        }

        if (layerName === 'Claim / Write Gate') {
            const policy = (
                message.conversationMomentum ||
                workflowMetadata?.conversationMomentum ||
                (message.safetyRoute as any)?.policy
            ) as any;

            const alignment = (message.alignment || workflowMetadata?.alignment) as any;

            return status === 'skipped'
                ? ['No claim/write gate check was needed for this response.']
                : [
                    'Checked whether the response could continue with enough support.',
                    'Reviewed alignment and grounding before generation.',
                    alignment?.threshold !== undefined
                        ? `Alignment threshold: ${alignment.threshold}`
                        : 'Alignment threshold was not returned to the UI.',
                    alignment?.supportThreshold !== undefined
                        ? `Support threshold: ${alignment.supportThreshold}`
                        : 'Support threshold was not returned to the UI.',
                    policy?.topicStatus ? `Topic status: ${policy.topicStatus}` : 'Topic status was not returned to the UI.',
                    policy?.momentumState ? `Conversation momentum: ${policy.momentumState}` : 'Conversation momentum was not returned to the UI.',
                    policy?.shouldAskFollowUp !== undefined
                        ? `Should ask follow-up: ${policy.shouldAskFollowUp ? 'Yes' : 'No'}`
                        : 'Follow-up decision was not returned to the UI.',
                    policy?.followUpStyle ? `Follow-up style: ${policy.followUpStyle}` : 'Follow-up style was not returned to the UI.',
                    policy?.policyConfidence !== undefined
                        ? `Policy confidence: ${policy.policyConfidence}`
                        : 'Policy confidence was not returned to the UI.',
                    policy?.policyRationale ? `Rationale: ${policy.policyRationale}` : 'Policy rationale was not returned to the UI.',
                ];
        }

        if (layerName === 'Response Generation') {
            return [
                'Generated the assistant response.',
                'Streamed the response text back to the UI.',
                'Completed response generation.',
            ];
        }

        return [
            'Reviewed the final response.',
            'Prepared final sources and metadata when available.',
            'Completed the workflow.',
        ];
    };

    const getWorkflowLayers = (message: ChatUiMessage): WorkflowLayer[] => {
        const trail = message.agenticProgressTrail || [];
        const layerMap = new Map<string, string[]>();

        trail.forEach((step) => {
            const layerName = getWorkflowLayerName(step);
            if (!layerName) return;

            const currentDetails = layerMap.get(layerName) || [];

            if (!currentDetails.includes(step)) {
                layerMap.set(layerName, [...currentDetails, step]);
            }
        });

        const retrievedCount = message.retrievedChunkIds?.length ?? 0;
        const usedCount = message.usedChunkIds?.length ?? 0;
        //const retrievalWasChecked = Array.isArray(message.retrievedChunkIds);
        
        if (retrievedCount > 0) {
            layerMap.set('Retrieval', [
                `Retrieved ${retrievedCount} context chunk${retrievedCount === 1 ? '' : 's'}.`,
                `Used ${usedCount} chunk${usedCount === 1 ? '' : 's'} in the final response.`,
                'Prepared retrieved context for response grounding.',
            ]);
        }

        if (retrievedCount > 0) {
            layerMap.set('Re-ranking / Context Selection', [
                `Reviewed ${retrievedCount} retrieved context chunk${retrievedCount === 1 ? '' : 's'}.`,
                `Selected ${usedCount} chunk${usedCount === 1 ? '' : 's'} for the final response.`,
                'Prepared the strongest supporting context for generation.',
            ]);
        }

        const preferredOrder = [
            'Request',
            'Safety Check',
            'Retrieval',
            'Re-ranking / Context Selection',
            'Claim / Write Gate',
            'Response Generation',
            'Final Review',
        ];

        const latestLayerName = trail.length
            ? getWorkflowLayerName(trail[trail.length - 1])
            : null;

        const hasRetrievalSignal =
            trail.some((step) => getWorkflowLayerName(step) === 'Retrieval') ||
            retrievedCount > 0;

        return preferredOrder.map((layerName) => {
            const rawDetails = layerMap.get(layerName) || [];

            let status: WorkflowLayerStatus = rawDetails.length > 0 ? 'complete' : 'pending';

            if (!message.isStreaming && rawDetails.length === 0) {
                const workflowMetadata = message.workflowMetadata as any;
                if (layerName === 'Request' || layerName === 'Response Generation' || layerName === 'Final Review') {
                    status = 'complete';
                } else if (layerName === 'Safety Check') {
                    if (message.safetyClassification || workflowMetadata?.safetyClassification || message.safetyRoute) {
                        status = 'complete';
                    }
                } else if (layerName === 'Retrieval') {
                    const retrievalSummary = message.retrievalSummary || workflowMetadata?.retrievalSummary;
                    const retrievalDecision = message.retrievalDecision || workflowMetadata?.retrievalDecision;
                    const totalRetrieved = retrievalSummary?.retrievedChunkCount ?? retrievedCount;

                    if (totalRetrieved > 0 || retrievalDecision?.isFactQuery === true) {
                        status = 'complete';
                    } else {
                        status = 'skipped';
                    }
                } else if (layerName === 'Re-ranking / Context Selection') {
                    const retrievalSummary = message.retrievalSummary || workflowMetadata?.retrievalSummary;
                    const totalRetrieved = retrievalSummary?.retrievedChunkCount ?? retrievedCount;

                    if (totalRetrieved > 0) {
                        status = 'complete';
                    } else {
                        status = 'skipped';
                    }
                } else if (layerName === 'Claim / Write Gate') {
                    if (message.conversationMomentum || workflowMetadata?.conversationMomentum || message.alignment || workflowMetadata?.alignment) {
                        status = 'complete';
                    } else {
                        status = 'skipped';
                    }
                }
            }

            if (message.isStreaming && latestLayerName === layerName) {
                status = 'active';
            }

            if (
                message.isStreaming &&
                layerName === 'Retrieval' &&
                hasRetrievalSignal &&
                latestLayerName === 'Retrieval'
            ) {
                status = 'active';
            }

            const details = getDefaultWorkflowDetails(layerName, status, message);

            return {
                name: layerName,
                status,
                details,
            };
        });
    };

    useEffect(() => {
        if (message.isStreaming) {
            setWorkflowOpen(true);
        } else {
            setWorkflowOpen(false);
        }
    }, [message.isStreaming, message.id]);


   useEffect(() => {
        if (!message.isStreaming || !message.agenticProgressTrail?.length) return;

        const layers = getWorkflowLayers(message);
        const activeLayer = layers.find((layer) => layer.status === 'active');

        if (!activeLayer) return;

        const layerId = `${message.id}-${activeLayer.name}`;
        setExpandedWorkflowLayer(layerId);

        const timer = window.setTimeout(() => {
            setExpandedWorkflowLayer(null);
        }, 2600);
        //}, 1200);

        return () => window.clearTimeout(timer);
    }, [message.agenticProgressTrail, message.id, message.isStreaming]);

    useEffect(() => {
        if (!message.isStreaming) {
            setShowPlanningTrace(false);
            setPlanningTraceIndex(0);
            return;
        }

        setShowPlanningTrace(true);
        setPlanningTraceIndex(0);

        const messages = [
            'Loading...',
            'Planning next moves',
            'Checking safety, intent, and available context',
            'Preparing workflow layers',
        ];

        const interval = window.setInterval(() => {
            setPlanningTraceIndex((current) => {
                if (current >= messages.length - 1) {
                    window.clearInterval(interval);

                    window.setTimeout(() => {
                        setShowPlanningTrace(false);
                    }, 900);

                    return current;
                }

                return current + 1;
            });
        }, 1400);

        return () => window.clearInterval(interval);
    }, [message.id, message.isStreaming]);

    return (
        <urlContext.Provider value={{ url, setUrl }}>
            <highlightTextContext.Provider value={{ highlightText, setHighlightText }}>
                <sourceContext.Provider value={{ source, setSource }}>
                    <citationViewerOpenContext.Provider value={{ citationViewerOpen, setCitationViewerOpen }}>
                        <>
                            {audioOpen && audioEl && createPortal(
                                <AudioPlayerPopout
                                    audioElement={audioEl}
                                    defaultOpen={true}
                                    onClose={handleAudioClose}
                                />,
                                document.getElementById('root') ?? document.body
                            )}

                            <div className={`message-wrapper ${message.role}${message.isVariant ? ' variant-stream' : ''}`}>
                                <div className="message-avatar">
                                    {message.role === 'user' ? (
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: 'var(--color-sage)',
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                color: 'white'
                                            }}
                                        >
                                            {userInfo?.initial}
                                        </Avatar>
                                    ) : (
                                        <Avatar
                                            src={persona?.imageURL || persona?.avatar_url}
                                            alt={persona?.name}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: 'var(--color-accent)',
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                border: '1.5px solid var(--color-accent-soft)',
                                                color: 'white'
                                            }}
                                        >
                                            {persona?.name?.charAt(0).toUpperCase() || 'A'}
                                        </Avatar>
                                    )}
                                </div>
                                <div className="message-content">
                                    {message.isVariant && (
                                        <div className="variant-stream-label">
                                            {message.comparisonStreamLabel || 'Comparison stream'}
                                        </div>
                                    )}
                                    <div className="message-bubble">
                                        {message.role === 'assistant' ? (
                                            message.isStreaming &&
                                            !stripExtraTags(message.content).trim() &&
                                            isScoutPersona(persona) ? (
                                                <ScoutPondering profile={resolveScoutGuideProfile(persona)} />
                                            ) : message.isStreaming && !stripExtraTags(message.content).trim() && !showWorkflowSteps ? (
                                                <div className="agentic-progress-bubble-dots">
                                                    <TypingDots />
                                                </div>
                                            ) : (
                                                <MarkdownContent
                                                    content={stripExtraTags(message.content) + (message.isStreaming ? ' <span class="streaming-cursor">▊</span>' : '')}
                                                    sources={message.sources || {}}
                                                />
                                            )
                                        ) : (
                                            <>
                                                {stripExtraTags(message.content)}
                                                {message.isStreaming && (
                                                    <span className="streaming-cursor">▊</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {message.role === 'assistant' && message.interrupted && (
                                        <div className="stream-interrupted-notice" role="status">
                                            {STREAM_INTERRUPTED_NOTICE}
                                        </div>
                                    )}

                                    {message.role === 'assistant' && (showWorkflowSteps ? (
                                        message.isStreaming ||
                                        Boolean(message.agenticProgressTrail?.length) ||
                                        Boolean(message.retrievedChunkIds?.length) ||
                                        Boolean(message.workflowMetadata) ||
                                        Boolean(message.safetyClassification) ||
                                        Boolean(message.retrievalDecision) ||
                                        Boolean(message.conversationMomentum) ||
                                        Boolean(message.alignment)
                                    ? (
                                    <div className="agentic-progress-wrapper">
                                        <button
                                            type="button"
                                            className="workflow-steps-toggle"
                                            onClick={() => setWorkflowOpen((current) => !current)}
                                        >
                                            {workflowOpen ? '⌄ Agentic workflow' : '› Agentic workflow'}
                                        </button>

                                        {workflowOpen && (() => {
                                            const allLayers = getWorkflowLayers(message);

                                            const layersToRender: WorkflowLayer[] = allLayers.filter((layer) => {
                                                if (layer.status === 'active') return true;
                                                if (layer.status === 'complete') return true;
                                                if (!message.isStreaming && layer.status === 'skipped') return true;
                                                return false;
                                            });

                                            return (
                                                <div className="agentic-progress-inline progressive-layer-mode">
                                                        {showPlanningTrace && message.isStreaming && (
                                                            <div className="agentic-planning-trace">
                                                                <div className="agentic-planning-line active">
                                                                    {[
                                                                        'Loading...',
                                                                        'Planning next moves',
                                                                        'Checking safety, intent, and available context',
                                                                        'Preparing workflow layers',
                                                                    ][planningTraceIndex]}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {layersToRender.map((layer) => {
                                                            const layerId = `${message.id}-${layer.name}`;
                                                            const isExpanded = expandedWorkflowLayer === layerId;

                                                            return (
                                                                <div key={layerId} className="agentic-progress-item">
                                                                    <button
                                                                        type="button"
                                                                        className={`agentic-progress-step ${layer.status} ${isExpanded ? 'expanded' : ''}`}
                                                                        onClick={() =>
                                                                            setExpandedWorkflowLayer((current) =>
                                                                                current === layerId ? null : layerId
                                                                            )
                                                                        }
                                                                    >
                                                                        <span className="agentic-progress-layer-name">
                                                                            {layer.name}
                                                                        </span>

                                                                        <span className={`agentic-progress-status ${layer.status}`}>
                                                                            {layer.status === 'active'
                                                                                ? 'Running'
                                                                                : layer.status === 'skipped'
                                                                                    ? 'Skipped'
                                                                                    : layer.status === 'pending'
                                                                                        ? 'Pending'
                                                                                        : 'Done'}
                                                                        </span>

                                                                        <span className="agentic-progress-chevron">
                                                                            ⌄
                                                                        </span>
                                                                    </button>

                                                                    {isExpanded && (
                                                                        <div className="agentic-progress-details">
                                                                            {layer.details.map((detail, detailIndex) => (
                                                                                <div
                                                                                    key={`${layerId}-detail-${detailIndex}`}
                                                                                    className="agentic-progress-detail"
                                                                                >
                                                                                    {detail}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    );
                                                 })()}
                                            </div>
                                        ) : null
                                    ) : null)}
                                        {message.role === 'user' && (
                                        <div className="assistant-message-meta">
                                            {showClassification && message.safetyRoute && (
                                                <ClassificationBadge safetyRoute={message.safetyRoute} compact />
                                            )}
                                            {showClassification &&
                                                isWebSearchResponseMode(message.responseMode) && (
                                                    <div className="classification-badge-container compact">
                                                        <WebSearchBadge />
                                                    </div>
                                                )}
                                            {showClassification && wasSynapseUsed(message) && (
                                                <div className="classification-badge-container compact">
                                                    <SynapseBadge personaName={persona?.name} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {message.role === 'assistant' && !message.isStreaming && !message.isPreamble && (
                                        <div
                                            className="feedback-wrapper"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                            }}
                                        >
                                            {/* AUDIO BUTTON */}
                                            <IconButton
                                                size="small"
                                                sx={{ mt: '2px' }}
                                                onClick={() => {
                                                    if (currentlyPlayingId === message.id) {
                                                        onStopSpeak?.();
                                                    } else {
                                                        onSpeak?.(message.content, message.exchangeId, message.streamChatId || chatId);
                                                    }
                                                }}
                                            >
                                                {currentlyPlayingId === message.id ? (
                                                    <StopIcon fontSize="small" />
                                                ) : (
                                                    <VolumeUpIcon fontSize="small" />
                                                )}
                                            </IconButton>

                                            {/* FEEDBACK BUTTONS */}
                                            <FeedbackButtons
                                                exchangeId={message.exchangeId || message.id}
                                                mode={message.feedbackMode ?? 'TEXT'}
                                                initialCategories={message.feedbackCategory}
                                                initialComment={message.comment}
                                                seeds={message.seeds}
                                                initialRating={message.rating}
                                                onViewGraph={() => setGraphModalOpen(true)}
                                                toolTrace={message.toolTrace}
                                                showToolTraceIcon={isWebSearchResponseMode(message.responseMode)}
                                                onFeedback={(exchangeId, kind, comment, categories) => onFeedback?.(
                                                    exchangeId,
                                                    kind,
                                                    comment,
                                                    message.streamChatId || chatId,
                                                    categories,
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Subgraph Modal */}
                            {message.seeds && message.seeds.length > 0 && (
                                <SubgraphModal
                                    open={graphModalOpen}
                                    onClose={() => setGraphModalOpen(false)}
                                    seedIds={message.seeds}
                                    retrievedIds={message.retrievedChunkIds}
                                    usedIds={message.usedChunkIds}
                                    tenantId={persona?.tenantId || 'chip'}
                                    title="Knowledge Graph for This Response"
                                />
                            )}

                            <DocxViewer url={url} highlightText={highlightText} source={source} />
                        </>
                    </citationViewerOpenContext.Provider>
                </sourceContext.Provider>
            </highlightTextContext.Provider>
        </urlContext.Provider>
    );
};
